/**
 * Core ConfirmDialog Component
 *
 * Pre-configured confirmation dialog for destructive actions (delete, revoke, etc.)
 * with variant-driven styling, loading state, and optional description.
 *
 * Layout: Icon (top-left, bordered box) → Title → Description → Equal-width buttons
 *
 * @example
 *   <ConfirmDialog
 *     open={showConfirm}
 *     onClose={() => setShowConfirm(false)}
 *     onConfirm={handleDelete}
 *     variant="danger"
 *     title="Delete Data Source"
 *     description="This will permanently remove the data source and all associated models."
 *     confirmText="Delete"
 *   />
 */
import React from 'react';
import {
  Dialog, IconButton, CircularProgress,
} from '@mui/material';
import { X, AlertTriangle, Info, CheckCircle2, Trash2 } from 'lucide-react';

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  /** Title */
  title: string;
  /** Description text */
  description?: React.ReactNode;
  /** Confirm button text */
  confirmText?: string;
  /** Cancel button text */
  cancelText?: string;
  /** Variant — drives icon and confirm button color */
  variant?: 'danger' | 'warning' | 'info' | 'success';
  /** Loading state for async confirm actions */
  loading?: boolean;
  /** Custom icon override */
  icon?: React.ReactNode;
  /** Disable backdrop close during loading */
  disableBackdropClose?: boolean;
}

const variantConfig = {
  danger: {
    icon: <Trash2 className="h-5 w-5" />,
    iconColor: '#B91C1C',
    confirmBg: '#DC2626',
    confirmHover: '#B91C1C',
  },
  warning: {
    icon: <AlertTriangle className="h-5 w-5" />,
    iconColor: '#B45309',
    confirmBg: '#D97706',
    confirmHover: '#B45309',
  },
  info: {
    icon: <Info className="h-5 w-5" />,
    iconColor: '#1D4ED8',
    confirmBg: '#2563EB',
    confirmHover: '#1D4ED8',
  },
  success: {
    icon: <CheckCircle2 className="h-5 w-5" />,
    iconColor: '#047857',
    confirmBg: '#059669',
    confirmHover: '#047857',
  },
};

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  loading = false,
  icon: customIcon,
  disableBackdropClose = false,
}) => {
  const config = variantConfig[variant];

  const handleConfirm = async () => {
    await onConfirm();
    if (!loading) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={loading || disableBackdropClose ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 'var(--border-radius, 12px)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 4px 16px rgba(0,0,0,0.08)',
        },
      }}
    >
      {/* ── Icon (white bordered box) + Close × ── */}
      <div style={{ position: 'relative', padding: '24px 24px 0 24px' }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 44,
            height: 44,
            borderRadius: 'var(--border-radius, 8px)',
            border: '1px solid #e2e8f0',
            color: config.iconColor,
            backgroundColor: '#fff',
          }}
        >
          {customIcon || config.icon}
        </div>
        <IconButton
          onClick={onClose}
          size="small"
          disabled={loading}
          sx={{
            position: 'absolute',
            right: 20,
            top: 20,
            color: '#94a3b8',
            '&:hover': { color: '#475569', backgroundColor: '#f1f5f9' },
          }}
        >
          <X className="h-4 w-4" />
        </IconButton>
      </div>

      {/* ── Title + Description ── */}
      <div style={{ padding: '16px 24px 8px 24px' }}>
        <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>
          {title}
        </div>
        {description && (
          <div style={{ fontSize: '0.875rem', color: '#64748b', marginTop: 8, lineHeight: 1.6 }}>
            {description}
          </div>
        )}
      </div>

      {/* ── Equal-width action buttons ── */}
      <div style={{ display: 'flex', gap: 12, padding: '12px 24px 24px 24px' }}>
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          style={{
            flex: 1,
            height: 44,
            border: '1px solid #e2e8f0',
            borderRadius: 'var(--border-radius, 8px)',
            backgroundColor: '#fff',
            color: '#1e293b',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.5 : 1,
            transition: 'all 150ms ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
        >
          {cancelText}
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={loading}
          style={{
            flex: 1,
            height: 44,
            border: 'none',
            borderRadius: 'var(--border-radius, 8px)',
            backgroundColor: config.confirmBg,
            color: '#fff',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            transition: 'all 150ms ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
          }}
          onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = config.confirmHover; }}
          onMouseLeave={(e) => { if (!loading) e.currentTarget.style.backgroundColor = config.confirmBg; }}
        >
          {loading && <CircularProgress size={16} sx={{ color: '#fff' }} />}
          {confirmText}
        </button>
      </div>
    </Dialog>
  );
};
export default ConfirmDialog;

